import { Ref, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { isMobile, isTablet } from "react-device-detect";
import MobileTankBatteryView, { MobileTankBatteryViewType } from "./MobileTankBatteryView";
import DesktopTankBatteryView, { DesktopTankBatteryViewType } from "./DesktopTankBatteryView";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useTankRelationshipData } from "../../../queries/tank-relationship-data/UseTankRelationshipData";
import { useSummaryListingContext } from "../SummaryListingContext";
import { useTankBatterySummaryData } from "../../../queries/group-summary/tank-batteries/UseTankBatterySummaryData";
import { subscribe, unsubscribe } from "pubsub-js";
import { EXECUTE_ACTION_TOPIC } from "../../../common/models/pub-sub/PubSubTopics";
import { useEvaluateFeature } from "../../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../../services/features-data-service/Models";

export type TankBatteryViewProps = {
    visible?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type TankBatteryViewType = {
    executeAction: (action: BaseActionEnum) => void;
};

function TankBatteryView({
    visible,
    setViewOptionState
}: TankBatteryViewProps, ref: Ref<unknown> | undefined) {


    const isPhone = isMobile && !isTablet;
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();

    const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.HeaderAndActionsV2,
    });

    const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);


    const { 
        setTankBatteryCount 
    } = useSummaryListingContext();


    // only needed for the count
    const {data: tankPads } = useQueryNotifications(useTankRelationshipData)({
        groupID: routeContext.groupID as number,
        enabled: !isPhone
    }); 
    
    const { data: summaryData } = useQueryNotifications(useTankBatterySummaryData)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        offline: offlineState.offline,
        enabled: isPhone,
    });


    const desktopTankBatteryView = useRef<DesktopTankBatteryViewType>(undefined);
    const mobileTankBatteryView = useRef<MobileTankBatteryViewType>(undefined);

    useImperativeHandle<unknown, TankBatteryViewType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    function executeAction(action: BaseActionEnum) {
        
        if (isPhone) {
            mobileTankBatteryView.current?.executeAction(action);
        } else {
            desktopTankBatteryView.current?.executeAction(action);
        }
    }


    useEffect(() => {
        if (tankPads && !isPhone) {
            setTankBatteryCount(tankPads.flatMap(o => o.tankBatteries).length);
        }
    }, [
        tankPads, 
        setTankBatteryCount,
        isPhone
    ]);

    useEffect(() => {
        if (isPhone) {
            const rowCount = summaryData?.rows.length ?? 0;
            setTankBatteryCount(rowCount);
        }

    }, [
        summaryData, 
        setTankBatteryCount, 
        isPhone    
    ]);

    // useEffect(() => {

    //     if (headerAndActionsV2 && visible) {

    //         const token = subscribe(EXECUTE_ACTION_TOPIC, (message, data) => 
    //             alert(`action: ${data.action}`)
    //         );
    
    //         return () => {
    //             if (token) {
    //                 unsubscribe(token);
    //             }
    //         }
    //     }

    // }, [
    //     headerAndActionsV2,
    //     visible
    // ]);


    return (
        <>
            {
                visible ?
                    isPhone ? 
                        <MobileTankBatteryView 
                            ref={mobileTankBatteryView}
                            setViewOptionState={setViewOptionState}
                        /> :
                        <DesktopTankBatteryView 
                            ref={desktopTankBatteryView}
                            setViewOptionState={setViewOptionState}
                        />: 
                    null

            }
        </>
    );
}

export default forwardRef(TankBatteryView);